import gsap from 'gsap';
import type { RefObject } from 'react';
import { useEffect, useRef } from 'react';
import type {
  ThemeColor,
  ThemeColorProviderContextType,
} from '../components/unlisted/ThemeColorProvider';
import { useThemeColorProviderContext } from '../components/unlisted/ThemeColorProvider';
import useIsomorphicLayoutEffect from './useIsomorphicLayoutEffect';
import type { ScrollProviderContextType } from '../components/unlisted/ScrollProvider';
import { useScrollProviderContext } from '../components/unlisted/ScrollProvider';
import { getDefaultScrollTriggerVars } from '../util/scroll/scrollTrigger.utils';

function useThemeScroll(
  isDarkTheme: boolean | undefined,
  accentForeground?: string,
  accentBackground?: string,
  refreshKey?: unknown,
): void {
  const {
    setDarkTheme,
    setAccentThemeColor,
    themeWrapperRef,
    accentThemeColor,
    primaryThemeColor,
    blocks,
    forceRefresh,
  } = useThemeColorProviderContext() as ThemeColorProviderContextType;
  const scrollContext = useScrollProviderContext() as ScrollProviderContextType;
  const setBackgroundColor = useRef(
    gsap.quickSetter(themeWrapperRef.current, '--background-color'),
  );
  const setForegroundColor = useRef(
    gsap.quickSetter(themeWrapperRef.current, '--foreground-color'),
  );

  useIsomorphicLayoutEffect(() => {
    setDarkTheme(typeof isDarkTheme === 'boolean' ? isDarkTheme : true);
    const accentTheme =
      accentForeground && accentBackground
        ? {
            background: accentBackground,
            foreground: accentForeground,
          }
        : undefined;
    setAccentThemeColor(accentTheme);
  }, [isDarkTheme, accentForeground, accentBackground, setDarkTheme, setAccentThemeColor]);

  useEffect(() => {
    const scrollAnimations: gsap.core.Animation[] = [];
    const scrollPoints: {
      ref: RefObject<HTMLElement | null>;
      theme: ThemeColor | null;
      previousTheme: ThemeColor | null;
    }[] = [];
    let previousTheme!: ThemeColor | null;
    // Edited blocks re-register at the end of the Set; transitions follow page order.
    const orderedBlocks = Array.from(blocks).sort((a, b) => {
      if (!a.ref.current || !b.ref.current) return 0;
      const position = a.ref.current.compareDocumentPosition(b.ref.current);
      if (position & Node.DOCUMENT_POSITION_DISCONNECTED) return 0;
      if (position & Node.DOCUMENT_POSITION_FOLLOWING) return -1;
      if (position & Node.DOCUMENT_POSITION_PRECEDING) return 1;
      return 0;
    });
    orderedBlocks.forEach((block) => {
      const accentColor = block.accentColor ?? accentThemeColor.current;
      const theme = block.useAccent ? accentColor : primaryThemeColor.current;

      if (!previousTheme) {
        previousTheme = primaryThemeColor.current;
      }

      if (
        previousTheme?.foreground !== theme?.foreground ||
        previousTheme?.background !== theme?.background
      ) {
        scrollPoints.push({
          previousTheme,
          theme,
          ref: block.ref,
        });
      }

      previousTheme = theme;
    });

    // Always set primaryThemeColor first, will be overwritten by scrollTrigger points.
    gsap.set(themeWrapperRef.current, {
      '--background-color': primaryThemeColor?.current?.background,
      '--foreground-color': primaryThemeColor?.current?.foreground,
    });

    scrollPoints.forEach((point) => {
      const fromColors = point.previousTheme || point.theme;
      const colors = {
        background: fromColors?.background,
        foreground: fromColors?.foreground,
      };

      const prevColors = {
        background: fromColors?.background,
        foreground: fromColors?.foreground,
      };

      scrollAnimations.push(
        gsap.fromTo(
          colors,
          { ...fromColors },
          {
            ...point.theme,
            ease: 'Power2.easeOut',
            repeatRefresh: true,
            scrollTrigger: {
              ...getDefaultScrollTriggerVars({
                trigger: point.ref.current,
                scrub: true,
                // If the start value below is changed, please double check ArticleHeader
                // and HeaderFullWidthTitle can still transition background colors properly on scroll
                start: 'top 60%',
                end: 'top 20%',
                scroller: scrollContext?.hasSmoothScroll?.current
                  ? scrollContext?.scrollWrapperRef?.current
                  : undefined,
              }),
              onUpdate() {
                if (colors?.background !== prevColors.background) {
                  setBackgroundColor.current(colors?.background);
                  prevColors.background = colors.background;
                }

                if (colors?.foreground !== prevColors?.foreground) {
                  setForegroundColor.current(colors?.foreground);
                  prevColors.foreground = colors.foreground;
                }
              },
            },
          },
        ),
      );
    });

    return () => {
      scrollAnimations.forEach((animation) => animation.kill());
    };
  }, [
    blocks,
    accentThemeColor,
    primaryThemeColor,
    themeWrapperRef,
    scrollContext.scrollWrapperRef,
    scrollContext?.hasSmoothScroll,
    forceRefresh,
    isDarkTheme,
    accentForeground,
    accentBackground,
    refreshKey,
  ]);
}

export default useThemeScroll;
